import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import PasswordInput from '../components/common/PasswordInput';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 20px;
`;

const Card = styled.div`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-top: 5px solid var(--primary-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Title = styled.h1`
  color: var(--primary-color);
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  margin-bottom: 32px;
  font-size: 16px;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  text-align: right;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 16px;
  direction: rtl;
  text-align: right;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(0, 142, 162, 0.1);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px 16px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--primary-hover);
  }

  &:disabled {
    background-color: var(--text-tertiary);
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ type: 'error' | 'success' }>`
  background-color: ${props => props.type === 'error' ? 'var(--error-light)' : 'var(--success-light)'};
  color: ${props => props.type === 'error' ? 'var(--error-color)' : 'var(--success-color)'};
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  text-align: right;
  border: 1px solid ${props => props.type === 'error' ? 'var(--error-color)' : 'var(--success-color)'};
`;

const LinkContainer = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const StyledLink = styled(Link)`
  color: var(--primary-color);
  text-decoration: none;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`;

const PasswordRequirements = styled.div`
  background-color: var(--background-color);
  padding: 12px;
  border-radius: var(--border-radius-md);
  text-align: right;
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 8px;
`;

const ResetPasswordPage: React.FC = () => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { token } = useParams<{ token: string }>();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'סיסמה חדשה היא שדה חובה';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'הסיסמה חייבת להכיל לפחות 6 תווים';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'אישור סיסמה הוא שדה חובה';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'הסיסמאות אינן תואמות';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!validateForm() || !token) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await resetPassword({
        token,
        newPassword: formData.newPassword
      });

      if (result.isSuccess) {
        setMessage({
          type: 'success',
          text: result.message || 'הסיסמה עודכנה בהצלחה'
        });
        setIsSuccess(true);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setMessage({
          type: 'error',
          text: result.message || 'שגיאה באיפוס סיסמה'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'שגיאה באיפוס סיסמה'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <Container>
      <Card>
        <Title>איפוס סיסמה</Title>
        <Subtitle>
          הזן סיסמה חדשה לחשבון שלך
        </Subtitle>

        {message && (
          <Message type={message.type}>{message.text}</Message>
        )}

        {!isSuccess && (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="newPassword">סיסמה חדשה</Label>
              <PasswordInput
                inputComponent={Input}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                className={errors.newPassword ? 'error' : ''}
                disabled={isLoading}
                autoComplete="new-password"
              />
              {errors.newPassword && (
                <Message type="error">{errors.newPassword}</Message>
              )}
              <PasswordRequirements>
                הסיסמה חייבת להכיל לפחות 6 תווים
              </PasswordRequirements>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="confirmPassword">אישור סיסמה</Label>
              <PasswordInput
                inputComponent={Input}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={errors.confirmPassword ? 'error' : ''}
                disabled={isLoading}
                autoComplete="new-password"
              />
              {errors.confirmPassword && (
                <Message type="error">{errors.confirmPassword}</Message>
              )}
            </FormGroup>

            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'מעדכן...' : 'עדכן סיסמה'}
            </Button>
          </Form>
        )}

        {isSuccess && (
          <div>
            <p style={{ color: 'var(--text-secondary)', marginTop: '16px' }}>
              תועבר להתחברות בעוד מספר שניות...
            </p>
          </div>
        )}

        <LinkContainer>
          <StyledLink to="/login">
            חזור להתחברות
          </StyledLink>
        </LinkContainer>
      </Card>
    </Container>
  );
};

export default ResetPasswordPage;